import { Logger } from '@gitbook/util-debug';
import { type DiffStrategy, type DiffTreeChange, diffTrees } from '@gitbook/util-tree-merge';

import { type ComparePageOptions, comparePages } from '../pages';
import {
    type Revision,
    type RevisionPage,
    type RevisionPageLink,
    RevisionPageType,
} from '../types';
import { isRevisionPageParent } from './mutations';

const logger = Logger('hive:revisions:diff');

/**
 * Diff status for a page.
 */
export enum PageDiffStatus {
    Added = 'added',
    Deleted = 'deleted',
    Modified = 'modified',
}

/** Page from a revision, as a tree data structure */
export interface RevisionTreePage<Page extends RevisionPage = RevisionPage> {
    renderKey: string;
    page: Page;
    pages: RevisionTreePage[];
    diff?: PageDiffStatus;
}

/** Result when diffing revisions pages */
export interface PagesDiffResult {
    pages: RevisionTreePage[];
    diffs: Map<string, DiffTreeChange<RevisionTreePage>>;
}

/**
 * Diff two revisions and return a set of pages
 */
export function diffRevisionPages(
    current: Revision,
    parent: Revision,
    options: {
        /**
         * If false, deleted pages will not be included in the result.
         * @default true
         */
        includeDeleted?: boolean;

        /**
         * If false, unchanged pages will not be included in the result.
         * @default true
         */
        includeUnchanged?: boolean;
    } & ComparePageOptions = {}
): PagesDiffResult {
    const { includeDeleted = true, includeUnchanged = true, ...compareOptions } = options;

    const diffStrategy: DiffStrategy<RevisionTreePage> = {
        getId: (node) => node.page.id,
        getChildren: (node) => node.pages,
        setChildren: (node, pages) => ({
            ...node,
            pages,
        }),
        compareItems: (original, modified) =>
            comparePages(original.page, modified.page, compareOptions) ? 1 : 0,
        annotateChange: (node, diff) => {
            if (!includeDeleted && diff.type === 'deleted') {
                return [];
            }
            if (diff.type === 'deleted' && current.pages[node.page.id]) {
                return node.pages;
            }

            return [
                {
                    ...node,
                    renderKey: `${node.renderKey}-${diff.type}`,
                    diff: diff.type as PageDiffStatus,
                },
            ];
        },
        shouldRecordChange: (node, diff) => {
            return !(diff.type === 'deleted' && current.pages[node.page.id]);
        },
        annotateUnchanged: (node, ctx) => {
            if (!includeUnchanged && !ctx.hasDescendantChanges) {
                return [];
            }

            return [node];
        },
    };

    const result = diffTrees(
        {
            before: createTreePages(parent.pages, parent.rootPages),
            after: createTreePages(current.pages, current.rootPages),
        },
        diffStrategy
    );

    return {
        pages: result.items,
        diffs: result.changes,
    };
}

/**
 * Create a tree from a set of pages.
 */
export function createTreePages(
    pages: { [key: string]: RevisionPage },
    rootPages: string[]
): RevisionTreePage[] {
    const tree: RevisionTreePage[] = [];

    rootPages.forEach((pageKey) => {
        const page = pages[pageKey];

        if (!page) {
            logger.warning(
                `Couldn't find page ${pageKey} although it is referenced. This may indicate a bad page tree normalization.`
            );
            return;
        }

        tree.push({
            renderKey: pageKey,
            page,
            pages:
                page.type === RevisionPageType.Link || page.type === RevisionPageType.Computed
                    ? []
                    : createTreePages(pages, page.pages),
        });
    });

    return tree;
}

/**
 * Flatten a tree of pages for a revision.
 */
export function flattenTreePages(tree: RevisionTreePage[]): {
    pages: { [key: string]: RevisionPage };
    rootPages: string[];
} {
    const pages: { [key: string]: RevisionPage } = {};
    const rootPages: string[] = tree.map((p) => p.page.id);

    function iterateNodes(nodes: RevisionTreePage[]) {
        nodes.forEach((node) => {
            pages[node.page.id] = { ...node.page };
            const page = pages[node.page.id];

            if (isRevisionPageParent(page)) {
                page.pages = node.pages.map((p) => p.page.id);
                iterateNodes(node.pages);
            }
        });
    }
    iterateNodes(tree);

    return { pages, rootPages };
}

/**
 * Test if a tree page is a link.
 */
export function isLinkTreePage(page: RevisionTreePage): page is RevisionTreePage<RevisionPageLink> {
    return page.page.type === RevisionPageType.Link;
}

/**
 * Return the ids of pages that have at least one descendant (at any depth) with a diff.
 */
export function getPagesWithChangedDescendant(pages: RevisionTreePage[]): Set<string> {
    const result = new Set<string>();
    const stack = pages.map((node) => ({ node, ancestorIds: [] as string[] }));

    let entry = stack.pop();
    while (entry) {
        const { node, ancestorIds } = entry;

        if (node.diff) {
            for (const id of ancestorIds) {
                result.add(id);
            }
        }

        const childAncestorIds = [...ancestorIds, node.page.id];
        for (const child of node.pages) {
            stack.push({ node: child, ancestorIds: childAncestorIds });
        }

        entry = stack.pop();
    }

    return result;
}
